/**
 * XP and levels.
 *
 * Each level costs a little more than the last: level 1 → 2 needs 100 XP,
 * 2 → 3 needs 130, and so on. Cheap to compute and easy to reason about when
 * balancing rewards.
 */

const BASE_COST = 100;
const GROWTH = 30;

/** XP needed to go from `level` to `level + 1`. */
export function costOfLevel(level) {
  return BASE_COST + (level - 1) * GROWTH;
}

/** Cumulative XP required to *reach* `level` (level 1 is free). */
export function totalXpForLevel(level) {
  let total = 0;
  for (let l = 1; l < level; l++) total += costOfLevel(l);
  return total;
}

/**
 * @returns {{level:number, into:number, needed:number, progress:number}}
 *   `into`/`needed` are XP within the current level; `progress` is 0–1.
 */
export function levelFromXp(xp) {
  const safeXp = Math.max(0, Math.floor(xp) || 0);
  let level = 1;
  let remaining = safeXp;

  while (remaining >= costOfLevel(level)) {
    remaining -= costOfLevel(level);
    level += 1;
  }

  const needed = costOfLevel(level);
  return { level, into: remaining, needed, progress: needed ? remaining / needed : 0 };
}

/** XP awards, kept in one place so the economy is easy to retune. */
export const XP_REWARDS = {
  verseRevealed: 3,
  verseMarkedLearning: 4,
  verseMarkedKnown: 10,
  quizCorrect: 10, // questions.js overrides this per difficulty
  quizPerfectBonus: 25,
  storyChapter: 20, // read a chapter through and answer its question
  storyChapterCorrect: 10, // on top, if the answer was right
  storyActComplete: 60, // finishing a whole act

  // Paid once, the first time somebody finishes the character quiz. Repeating
  // it is free and encouraged — it is the thing people re-take and send on —
  // but paying every time would make it the cheapest XP in the app.
  characterQuiz: 50,

  dailyVisit: 15,
  streakMilestone: 50,
};

/**
 * A short label for the level, purely cosmetic.
 *
 * The progression traces a scriptural arc rather than a generic skill ladder:
 * you set out (Pilgrim), you search (Seeker), you follow (Disciple), you
 * testify (Witness), you lead others (Shepherd), and finally you know the text
 * itself (Scribe).
 */
/**
 * The next rank, and the level it starts at.
 *
 * Titles change only at 3, 7, 12, 18 and 25, so levelTitle(level + 1) is
 * usually just the current title again — which would put "Pilgrim awaits" in
 * front of a Pilgrim. This walks forward to the next name that is actually
 * different, and returns null once there are none left.
 */
export function nextRank(level) {
  const current = levelTitle(level);
  for (let next = level + 1; next <= 40; next += 1) {
    const title = levelTitle(next);
    if (title !== current) return { title, atLevel: next };
  }
  return null;
}

export function levelTitle(level) {
  if (level >= 25) return 'Scribe';
  if (level >= 18) return 'Shepherd';
  if (level >= 12) return 'Witness';
  if (level >= 7) return 'Disciple';
  if (level >= 3) return 'Seeker';
  return 'Pilgrim';
}
